import { kitchenApi } from './kitchenApi';
import type { MenuItemDto, OrderDto, OrderKeyDto } from '../api-types';
import type { Statistics } from '../api-types/models/Statistics';

export const ordersApi = kitchenApi.injectEndpoints({
    endpoints: (builder) => ({
        getOrders: builder.query<OrderDto[], string>({
            query: (dateIso) => ({
                apiName: 'brunch-v2',
                path: `/order/user/${dateIso}`,
                method: 'GET'
            }),
            providesTags: ['Orders']
        }),

        getOrdersForMonth: builder.query<
            OrderDto[],
            { month: number; year: number }
        >({
            query: ({ month, year }) => ({
                apiName: 'brunch-v2',
                path: `/order/user/${month + 1}/${year}`,
                method: 'GET'
            }),
            providesTags: ['Orders']
        }),

        createOrder: builder.mutation<
            OrderDto[],
            { date: string; menuItem: MenuItemDto }
        >({
            query: ({ date, menuItem }) => ({
                apiName: 'brunch-v2',
                path: '/order/place',
                method: 'POST',
                body: {
                    date,
                    menuItemUuid: menuItem.uuid,
                    menuItemId: menuItem.id,
                    comment: ''
                }
            }),
            invalidatesTags: ['Orders']
        }),

        cancelOrder: builder.mutation<
            OrderDto[],
            { order: OrderDto; date: string }
        >({
            query: ({ order, date }) => ({
                apiName: 'brunch-v2',
                path: `/order/${date}/${order.id}/cancel`,
                method: 'PUT'
            }),
            invalidatesTags: ['Orders']
        }),

        commentOrder: builder.mutation<
            OrderDto[],
            { comment: string; order: OrderDto }
        >({
            query: ({ comment, order }) => ({
                apiName: 'brunch',
                path: '/orders/comment',
                method: 'PUT',
                body: { date: order.date, key: order.id, comment }
            }),
            invalidatesTags: ['Orders']
        }),

        getOrdersToRate: builder.query<OrderDto[], void>({
            query: () => ({
                apiName: 'brunch',
                path: '/orders/user/reviews',
                method: 'GET'
            }),
            providesTags: ['Orders']
        }),

        rateOrder: builder.mutation<
            void,
            { order: OrderKeyDto; rating: number; comment: string }
        >({
            query: ({ order, rating, comment }) => ({
                apiName: 'brunch',
                path: '/ratings/update',
                method: 'PUT',
                body: {
                    orderKey: order.key,
                    orderDate: order.date,
                    rating,
                    comment
                }
            }),
            invalidatesTags: ['Orders']
        }),

        getAllOrdersForDate: builder.query<Statistics[], string>({
            query: (date) => ({
                apiName: 'brunch',
                path: `/analytics/orders/count/${date}`,
                method: 'GET'
            }),
            providesTags: ['Analytics']
        }),

        getRepresentativeUsers: builder.query<string[], string>({
            query: (dateIso) => ({
                apiName: 'brunch',
                path: `/representative/users/${dateIso}`,
                method: 'GET'
            }),
            providesTags: ['Orders']
        }),

        getOrdersForGuest: builder.query<
            OrderDto[],
            { date: string; guest: string }
        >({
            query: ({ date, guest }) => ({
                apiName: 'brunch',
                path: `/representative/orders/user/${guest}/date/${date}`,
                method: 'GET'
            }),
            providesTags: ['Orders']
        }),

        createOrderForGuest: builder.mutation<
            OrderDto[],
            { date: string; menuItem: MenuItemDto; activeUser: string }
        >({
            query: ({ date, menuItem, activeUser }) => ({
                apiName: 'brunch',
                path: `/representative/order/${activeUser}`,
                method: 'POST',
                body: {
                    date,
                    menuItemUuid: menuItem.uuid,
                    menuItemId: menuItem.id,
                    comment: ''
                }
            }),
            invalidatesTags: ['Orders']
        }),

        commentOrderForGuest: builder.mutation<
            OrderDto[],
            { comment: string; order: OrderDto; activeUser: string }
        >({
            query: ({ comment, order, activeUser }) => ({
                apiName: 'brunch',
                path: `/representative/order/comment/${activeUser}`,
                method: 'PUT',
                body: { date: order.date, key: order.id, comment }
            }),
            invalidatesTags: ['Orders']
        })
    }),
    overrideExisting: true
});

export const {
    useGetOrdersQuery,
    useGetOrdersForMonthQuery,
    useCreateOrderMutation,
    useCancelOrderMutation,
    useCommentOrderMutation,
    useGetOrdersToRateQuery,
    useRateOrderMutation,
    useGetAllOrdersForDateQuery,
    useGetRepresentativeUsersQuery,
    useGetOrdersForGuestQuery,
    useCreateOrderForGuestMutation,
    useCommentOrderForGuestMutation
} = ordersApi;
